import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef,} from '@angular/material/dialog';
import {DataManagementAppService} from 'src/app/services/data-management-app.service';
import {SharedFeaturesService} from 'src/app/services/shared-features.service';
import {SolutionsAppService} from 'src/app/services/solutions-app.service';

import {FormControl} from '@angular/forms';
import {Observable, timer} from 'rxjs';
import {map, startWith} from 'rxjs/operators';
import {cloneDeep, isEqual} from 'lodash-es';
import {ShowDialogsComponent} from 'src/app/components/show-dialogs/show-dialogs.component';
import {JobSchedulerComponent} from 'src/app/components/job-scheduler/job-scheduler.component';

interface dateObjectType {
  format: string;
  example: string;
}

interface DataItem {
  column: string;
  question: string;
  datasetname: string;
  mapping: any;
}

interface GroupedData {
  [key: string]: DataItem[];
}

@Component({
  selector: 'app-solution-executor-form',
  templateUrl: './solution-executor-form.component.html',
  styleUrls: ['./solution-executor-form.component.scss'],
})
export class SolutionExecutorFormComponent implements OnInit {
  activeSubscription: any;
  usedDatasetsInVersion: any;
  groupedStep2: GroupedData = {};
  oldGroupedStep2: GroupedData = {};
  groupedStep3: any;
  oldGroupedStep3: any;
  configuredDatasets: any = [];
  formExist: boolean = false;
  existingFormData: any;
  myControl = new FormControl<string>('');
  myControl1 = new FormControl<string>('');
  supportedDateFormatsInPython: dateObjectType[] = [];
  filteredOptions: Observable<dateObjectType[]> | any;
  projectToSchedules: any;
  alreadyScheduledProjects: any;

  constructor(
    public dialogRef: MatDialogRef<SolutionExecutorFormComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    @Inject(MAT_DIALOG_DATA) public preview: boolean = true,
    private solutionServices: SolutionsAppService,
    private snackBar: SharedFeaturesService,
    private dataManagementService: DataManagementAppService,
    private dialog: MatDialog
  ) {
  }

  ngOnInit(): void {
    this.activeSubscription = this.data?.subscriptionDetails;

    this.preview = this.data?.preview;
    this.patchValue();
    if (this.data?.formData) {
      this.usedDatasetsInVersion = this.data?.formData['config_json']['step1'];
      this.usedDatasetsInVersion.sort((a: any, b: any) =>
        a?.data_name.localeCompare(b?.data_name)
      );
    }
    this.dataManagementService.getConfiguredDataSource().subscribe({
      next: (response: any) => {
        this.configuredDatasets = response;
      },
    });

    this.dataManagementService.getPythonDateFormats().subscribe({
      next: (response: any) => {
        this.supportedDateFormatsInPython = response['date_formats'];
      },
    });

    this.filteredOptions = this.myControl.valueChanges.pipe(
      startWith(''),
      map((value: any) => {
        const name = typeof value === 'string' ? value : value['format'];
        return name
          ? this._filter(name as string)
          : this.supportedDateFormatsInPython.slice();
      })
    );
    this.solutionServices
      .getProjectMarkedForSchedule(
        {solution_id: this.activeSubscription?.solution_id},
        {version_id: this.activeSubscription?.version_id}
      )
      .subscribe({
        next: (response: any) => {
          if (response.length > 0) {
            this.projectToSchedules = response;
            this.projectToSchedules.sort((a: any, b: any) => {
              return (
                new Date(a.created_dtm).getTime() -
                new Date(b.created_dtm).getTime()
              );
            });
            this.getScheduledProject();
          }
        },
        error: (error: any) => {
          console.error('an error occurred in get scheduler', error);
        },
      });
  }

  getScheduledProject() {
    this.solutionServices
      .getScheduledProject(
        this.data?.subscriptionDetails,
        this.data?.subscriptionDetails
      )
      .subscribe({
        next: (response: any) => {
          this.alreadyScheduledProjects = response;
          if (this.alreadyScheduledProjects.length > 0) {
            this.convertScheduler();
          }
        },
        error: (error: any) => {
          console.error('an error occurred', error);
        },
      });
  }

  /* ---------- progress / status helpers (header + section pills) ---------- */

  private isAnswered(mapping: any): boolean {
    if (Array.isArray(mapping)) return mapping.length > 0;
    return !!mapping;
  }

  private get step2Items(): DataItem[] {
    if (!this.groupedStep2 || typeof this.groupedStep2 !== 'object') return [];
    return Object.values(this.groupedStep2).flat();
  }

  get step2Total(): number {
    return this.step2Items.length;
  }

  get step2Answered(): number {
    return this.step2Items.filter((it) => this.isAnswered(it?.mapping)).length;
  }

  get step3Total(): number {
    return Array.isArray(this.groupedStep3) ? this.groupedStep3.length : 0;
  }

  get step3Answered(): number {
    if (!Array.isArray(this.groupedStep3)) return 0;
    return this.groupedStep3.filter((it: any) => this.isAnswered(it?.mapping))
      .length;
  }

  get schedTotal(): number {
    return this.projectToSchedules?.length || 0;
  }

  get schedAnswered(): number {
    return (
      this.projectToSchedules?.filter((p: any) => p?.scheduleData).length || 0
    );
  }

  get connectedDatasets(): number {
    return (
      this.usedDatasetsInVersion?.filter(
        (d: any) => this.getMappingValues(d?.data_name) != null
      ).length || 0
    );
  }

  get totalQuestions(): number {
    return this.step2Total + this.step3Total + this.schedTotal;
  }

  get answeredQuestions(): number {
    return this.step2Answered + this.step3Answered + this.schedAnswered;
  }

  get progressPct(): number {
    if (!this.totalQuestions) return 0;
    return Math.round((this.answeredQuestions / this.totalQuestions) * 100);
  }

  assignFilteredOption(data: any, value: any): any {
    const lowercasedValue = value.toLowerCase();
    let filteredData = data.filter((item: string) =>
      item.toLowerCase().includes(lowercasedValue)
    );
    filteredData = filteredData.length > 0 ? filteredData : [value];
    return filteredData;
  }

  patchValue() {
    this.solutionServices
      .getFormResponse(
        {solution_id: this.activeSubscription?.solution_id},
        {version_id: this.activeSubscription?.version_id},
        this.data?.formData['form_id']
      )
      .subscribe({
        next: (response: any) => {
          if (response[0]?.config_json) {
            this.existingFormData = response[0];
            this.groupedStep2 = cloneDeep(
              this.existingFormData['config_json']['step2']
            );
            this.oldGroupedStep2 = cloneDeep(
              this.existingFormData['config_json']['step2']
            );
            this.groupedStep3 = cloneDeep(
              this.existingFormData['config_json']['step3']
            );
            this.oldGroupedStep3 = cloneDeep(
              this.existingFormData['config_json']['step3']
            );
            this.formExist = true;
          } else {
            this.groupedStep2 = this.groupByDatasetName(
              this.data?.formData['config_json']['step2']
            );
            this.groupedStep3 = this.data?.formData['config_json']['step3'];
          }
        },
        error: () => {
          this.groupedStep2 = this.groupByDatasetName(
            this.data?.formData['config_json']['step2']
          );
          this.groupedStep3 = this.data?.formData['config_json']['step3'];
        },
      });
  }

  displayFn(dateFormat: dateObjectType): string {
    return dateFormat && dateFormat['format'] ? dateFormat['format'] : '';
  }

  setDataset(data: any) {
    this.dataManagementService.setDataSourceName(data['data_name']);
    this.dataManagementService.setDataType(data['category']);
  }

  groupByDatasetName(data: any): GroupedData {
    if (!data) return data;
    const groupedData: GroupedData = {};

    for (const item of data) {
      const {datasetname} = item;

      if (!groupedData[datasetname]) {
        groupedData[datasetname] = [];
      }

      groupedData[datasetname].push(item);
    }

    return groupedData;
  }

  getMappingValues(dataName: string): string[] | null {
    const dataItem = this.configuredDatasets.find(
      (item: any) => item.data_name === dataName
    );

    if (dataItem) {
      if (dataItem?.source__connection_type_id == 'Files_Upload_(xlsx)')
        return [];
      const mapping = dataItem.extra_info?.mapping;
      if (mapping) {
        return Object.values(mapping).sort((a: any, b: any) => {
          return a.localeCompare(b);
        }) as string[];
      }
    }

    return null; // Return null if data_name not found or mapping not available.
  }

  checkFormValidity(): boolean {
    const scheduleFlag = this.checkScheduleDataExists(this.projectToSchedules);
    if (this.groupedStep2) {
      for (const datasetName in this.groupedStep2) {
        if (this.groupedStep2.hasOwnProperty(datasetName)) {
          const entries = this.groupedStep2[datasetName];
          for (const entry of entries) {
            if (!entry.mapping) {
              return true;
            }
          }
        }
      }
    }
    return scheduleFlag;
  }

  closeDialogForm() {
    if (
      !this.preview &&
      (!isEqual(this.oldGroupedStep2, this.groupedStep2) ||
        !isEqual(this.oldGroupedStep3, this.groupedStep3))
    ) {
      const dialogRef = this.dialog.open(ShowDialogsComponent, {
        data: {
          title: 'Save your changes?',
          message: 'You have unsaved changes to this form.',
          confirmText: 'Save',
          cancelText: 'Discard',
        },
        disableClose: false,
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.save();
        } else {
          this.dialogRef.close();
        }
      });
    } else {
      this.dialogRef.close();
    }
  }

  save() {
    const formResponse = {
      form_id: this.data?.formData['form_id'],
      config_json: {step2: this.groupedStep2, step3: this.groupedStep3},
    };
    if (this.formExist) {
      this.solutionServices
        .updateFormResponse(
          {solution_id: this.activeSubscription?.solution_id},
          {version_id: this.activeSubscription?.version_id},
          this.data?.formData['form_id'],
          this.existingFormData['form_response_id'],
          formResponse
        )
        .subscribe({
          next: (response: any) => {
            this.snackBar.openSnackBar(
              'response updated successfully',
              'Close',
              'success-snackbar'
            );
            this.dialogRef.close(true);
          },
          error: (error) => {
            console.error(JSON.stringify(error));
            this.snackBar.openSnackBar(
              'An error occurred while updating response',
              'Close',
              'error-snackbar'
            );
          },
        });
    } else {
      this.solutionServices
        .saveFormResponse(
          {solution_id: this.activeSubscription?.solution_id},
          {version_id: this.activeSubscription?.version_id},
          this.data?.formData['form_id'],
          formResponse
        )
        .subscribe({
          next: (response: any) => {
            this.snackBar.openSnackBar(
              'Form response saved',
              'Close',
              'success-snackbar'
            );
            this.dialogRef.close(true);
          },
          error: (error) => {
            console.error(JSON.stringify(error));
            this.snackBar.openSnackBar(
              'An error occurred',
              'Close',
              'error-snackbar'
            );
          },
        });
    }
    this.scheduledProjects();
  }

  resetForm() {
    for (const key in this.groupedStep2) {
      if (this.groupedStep2.hasOwnProperty(key)) {
        const dataArray = this.groupedStep2[key];
        for (const item of dataArray) {
          if (item.column && item.mapping) {
            item.mapping = '';
          }
        }
      }
    }
    for (const obj of this.projectToSchedules) {
      if (obj.hasOwnProperty('scheduleData')) {
        delete obj['scheduleData'];
      }
    }
  }

  openScheduler(prj: any) {
    const formDialogRef = this.dialog.open(JobSchedulerComponent, {
      data: prj,
      disableClose: false,
      panelClass: 'job-scheduler-dialog',
    });
    formDialogRef.afterClosed().subscribe((result) => {
      if (result) {
        prj['scheduleData'] = result;
      }
    });
  }

  jsonToString(data: any): string {
    return JSON.stringify(data);
  }

  checkScheduleDataExists(array: any[]): boolean {
    if (!array) {
      return false;
    }
    for (const obj of array) {
      if (!obj.hasOwnProperty('scheduleData')) {
        return true;
      }
    }

    return false;
  }

  scheduledProjects() {
    this.projectToSchedules.forEach((element: any) => {
      if (!element?.scheduleData) {
        return; // Skip if scheduleData is not present
      }

      const {id, ...scheduleDataWithoutId} = element.scheduleData;

      if (id) {
        this.updateScheduledProject(element, id, scheduleDataWithoutId);
      } else {
        this.createScheduledProject(element, scheduleDataWithoutId);
      }
    });
  }

  convertScheduler(): any {
    this.projectToSchedules.forEach((element: any) => {
      const temp: any = this.snackBar.findObjectInArray(
        this.alreadyScheduledProjects,
        'scheduler_id',
        element?.scheduler_id
      );
      if (temp) {
        element['scheduleData'] = {
          id: temp?.id,
          name: temp?.name,
          minute: [temp?.crontab.minute],
          hour: [temp?.crontab?.hour],
          selectedDate:
            temp?.crontab.day_of_month === '*'
              ? null
              : temp?.crontab.day_of_month,
          selectedMonth:
            temp?.crontab.month_of_year === '*'
              ? null
              : temp?.crontab.month_of_year,
          selectedDays:
            temp?.crontab.day_of_week === '*'
              ? null
              : temp?.crontab.day_of_week,
          expires: temp?.expires,
          endOption: 'select_date',
          zone: 'UTC',
        };
      }
    });

    this.projectToSchedules.sort((a: any, b: any) => {
      if (a.hasOwnProperty('scheduleData') && !b.hasOwnProperty('scheduleData')) {
        return -1;
      }
      if (!a.hasOwnProperty('scheduleData') && b.hasOwnProperty('scheduleData')) {
        return 1;
      }
      return 0;
    });
  }

  private _filter(formatType: string): dateObjectType[] {
    const filterValue = formatType.toLowerCase();

    return this.supportedDateFormatsInPython.filter((option: any) =>
      option['format'].toLowerCase().includes(filterValue)
    );
  }

  private updateScheduledProject(
    element: any,
    idValue: any,
    scheduleDataWithoutId: any
  ) {
    this.solutionServices
      .putScheduledProject(
        this.activeSubscription,
        this.activeSubscription,
        element,
        idValue,
        scheduleDataWithoutId
      )
      .subscribe({
        next: (response: any) => {
          this.snackBar.openSnackBar(response, 'Close', 'success-snackbar');
        },
        error: (error: any) => {
          console.error(JSON.stringify(error));
          this.snackBar.openSnackBar(
            'An error occurred while scheduling project',
            'Close',
            'error-snackbar'
          );
        },
      });
  }

  private createScheduledProject(element: any, scheduleData: any) {
    this.solutionServices
      .postScheduledProject(
        this.activeSubscription,
        this.activeSubscription,
        element,
        scheduleData
      )
      .subscribe({
        next: (response: any) => {
          this.snackBar.openSnackBar(response, 'Close', 'success-snackbar');
        },
        error: (error: any) => {
          console.error(JSON.stringify(error));
          this.snackBar.openSnackBar(
            'An error occurred while scheduling project',
            'Close',
            'error-snackbar'
          );
        },
      });
  }
}
